import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../auth/context/AuthProvider";
import { useCompanyContext } from "../../common/company/CompanyContext";
import { GoldenSourceHelper } from "../../../app/Switch_Core_React_Standards";
import CompanyIdentityHeader from "../../common/company/CompanyIdentityHeader";
import DeclrAttributeGroupController from "./DeclrAttributeGroupController";

/**
 * 🎯 DECLARATION ATTRIBUTE GROUPS
 * Company builds named groups (e.g. "Transport") composed out of the
 * global Attribute Definition registry. These groups are what the Local
 * Declaration entry screen renders as sections to fill in.
 */
export default function DeclrAttributeGroupView() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { activeCompanyId, activeCompanyName } = useCompanyContext();
  const siteKey = process.env.REACT_APP_SITE_KEY;
  const SITE_NAME = process.env.REACT_APP_SITE_NAME || "sustina.nrgpix.ch";

  const {
    groups,
    allDefinitions,
    loading,
    error,
    newGroupLabel,
    setNewGroupLabel,
    creating,
    busyId,
    pickerGroupId,
    handleCreateGroup,
    handleRemoveGroup,
    togglePicker,
    handleAddMember,
    handleRemoveMember,
  } = DeclrAttributeGroupController(activeCompanyId, siteKey);

  const handleSignOut = async () => {
    await logout();
    navigate("/auth/sign-in");
  };

  if (!activeCompanyId) {
    navigate("/registered/dashboard");
    return null;
  }

  return (
    <main id="main" className="page-main" data-scroll-container>
      <div className="page-main-inner">
        <section className="page-section">
          <div className="container mt-4">
            <div className="row mb-2">
              <div className="col">
                <h2 className="h4 fw-bold mb-1">Declaration Attribute Groups</h2>
                <p className="text-muted small mb-0">group the attributes this company will capture locally</p>
              </div>
            </div>

            <CompanyIdentityHeader companyId={activeCompanyId} siteKey={siteKey} />

            {error && <div className="alert alert-danger extra-small">{error}</div>}

            <div className="card shadow-sm border-0 bg-white p-3 mb-3">
              <div className="d-flex gap-2">
                <input
                  className="form-control form-control-sm"
                  style={{ maxWidth: 320 }}
                  placeholder="New group name, e.g. Transport"
                  value={newGroupLabel}
                  onChange={(e) => setNewGroupLabel(e.target.value)}
                />
                <button
                  className="btn btn-sm btn-primary fw-bold"
                  disabled={creating || !newGroupLabel.trim()}
                  onClick={handleCreateGroup}
                >
                  {creating ? "CREATING..." : "NEW GROUP"}
                </button>
              </div>
            </div>

            {loading ? (
              <p className="text-muted extra-small italic">Loading...</p>
            ) : groups.length === 0 ? (
              <p className="text-muted extra-small italic">No groups yet — create one above.</p>
            ) : (
              <div className="row g-3">
                {groups.map((group) => {
                  const memberIds = new Set(group.members.map((m) => m.AttributeDefinitionId));
                  return (
                    <div className="col-md-6" key={group.AttributeGroupId}>
                      <div className="card shadow-sm border-0 bg-white overflow-hidden h-100">
                        <div className="p-3 border-bottom bg-light d-flex justify-content-between align-items-center">
                          <h6 className="extra-small fw-bold text-uppercase text-primary mb-0">{group.GroupLabel}</h6>
                          <div>
                            <button
                              className="btn btn-sm btn-outline-secondary fw-bold me-1"
                              onClick={() => togglePicker(group.AttributeGroupId)}
                            >
                              {pickerGroupId === group.AttributeGroupId ? "DONE" : "ADD ATTRIBUTE"}
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger fw-bold"
                              disabled={busyId === group.AttributeGroupId}
                              onClick={() => handleRemoveGroup(group.AttributeGroupId)}
                            >
                              REMOVE
                            </button>
                          </div>
                        </div>

                        <div className="p-3">
                          {group.members.length === 0 && (
                            <p className="text-muted extra-small italic mb-2">No attributes in this group yet.</p>
                          )}
                          <ul className="list-unstyled mb-0">
                            {group.members.map((m) => (
                              <li key={m.AttributeGroupMemberId} className="d-flex justify-content-between align-items-center py-1 border-bottom">
                                <span className="extra-small">
                                  {m.AttributeLabel} <span className="text-muted">({m.ValueDataType})</span>
                                </span>
                                <button
                                  className="btn btn-sm btn-link text-danger p-0"
                                  disabled={busyId === m.AttributeGroupMemberId}
                                  onClick={() => handleRemoveMember(m.AttributeGroupMemberId)}
                                >
                                  <i className="bi bi-x-circle"></i>
                                </button>
                              </li>
                            ))}
                          </ul>

                          {pickerGroupId === group.AttributeGroupId && (
                            <div className="mt-3 pt-2 border-top">
                              <div className="table-responsive" style={{ maxHeight: 220 }}>
                                <table className="table table-sm table-hover mb-0">
                                  <tbody>
                                    {allDefinitions
                                      .filter((d) => !memberIds.has(GoldenSourceHelper.get(d, "AttributeDefinitionId")))
                                      .map((d) => {
                                        const defId = GoldenSourceHelper.get(d, "AttributeDefinitionId");
                                        return (
                                          <tr key={defId}>
                                            <td className="extra-small">
                                              {GoldenSourceHelper.get(d, "Label")}{" "}
                                              <span className="text-muted">({GoldenSourceHelper.get(d, "ValueDataType")})</span>
                                            </td>
                                            <td className="text-end" style={{ width: 90 }}>
                                              <button
                                                className="btn btn-sm btn-outline-primary fw-bold"
                                                disabled={busyId === defId}
                                                onClick={() => handleAddMember(group.AttributeGroupId, defId, group.members.length)}
                                              >
                                                ADD
                                              </button>
                                            </td>
                                          </tr>
                                        );
                                      })}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* FOOTER NAVIGATION */}
            <div className="mt-5 pt-4 border-top d-flex justify-content-between align-items-center">
              <div className="d-flex gap-3">
                <button className="btn btn-link text-decoration-none p-0 text-primary fw-bold small" onClick={() => navigate("/registered/company-dashboard")}>
                  <i className="bi bi-arrow-left me-2"></i> Back to Dashboard
                </button>
                <button className="btn btn-link text-decoration-none p-0 text-primary fw-bold small" onClick={handleSignOut}>
                  <i className="bi bi-box-arrow-left me-2"></i> Sign Out
                </button>
              </div>
              <div className="text-end">
                <span className="text-muted extra-small d-block">Workflow Context</span>
                <span className="fw-bold small">{activeCompanyName || SITE_NAME}</span>
              </div>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}
